import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

export const CreateAccountPage = ({ onCreateAccount, onNavigate }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [message, setMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage(''); // Clear previous messages

        if (!firstName || !lastName || !email || !password || !confirmPassword || !inviteCode) {
            setMessage('All fields are required.'); // Keeping client-side checks
            return;
        }

        if (password !== confirmPassword) {
            setMessage('Passwords do not match.');
            return;
        }

        if (!agreedToTerms) {
            setMessage('You must agree to the Privacy Policy and Terms of Use to create an account.');
            return;
        }

        // Simple email validation
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setMessage('Invalid email format.');
            return;
        }

        // --- Firestore Invite Code Validation ---
        const codesRef = collection(db, "invite_codes");
        const q = query(codesRef, where("code", "==", inviteCode.trim()));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            setMessage('Invalid invite code.');
            return;
        }
        // --- End Validation ---

        // Get the data from the found document to check if it's an admin code
        const inviteCodeData = querySnapshot.docs[0].data();
        const roleToCreate = inviteCodeData.isForAdmin ? 'admin' : 'member';

        try {
            // Pass the determined role to the account creation function
            await onCreateAccount(email, password, firstName, lastName, roleToCreate);
            // Navigation will happen in App.js on success
        } catch (error) {
            if (error.code === 'auth/email-already-in-use') {
                setMessage('This email address is already in use.');
            } else if (error.code === 'auth/weak-password') {
                setMessage('The password is too weak. It must be at least 6 characters long.');
            } else {
                setMessage('An error occurred during account creation. Please try again.');
            }
        }
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
            <h2 className="page-title heading-primary-1">Create New Account</h2>
            <form onSubmit={handleSubmit} className="w-full max-w-md card p-8 rounded-lg shadow-md">
                <div className="mb-4">
                    <label htmlFor="firstName" className="field-label">First Name:</label>
                    <input type="text" id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                           className="input-field w-full py-2 px-3"
                           placeholder="Enter first name" required />
                </div>
                <div className="mb-4">
                    <label htmlFor="lastName" className="field-label">Last Name:</label>
                    <input type="text" id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)}
                           className="input-field w-full py-2 px-3"
                           placeholder="Enter last name" required />
                </div>
                <div className="mb-4">
                    <label htmlFor="email" className="field-label">Email:</label>
                    <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)}
                           className="input-field w-full py-2 px-3"
                           placeholder="your@example.com" required />
                </div>
                <div className="mb-4">
                    <label htmlFor="password" className="field-label">Password:</label>
                    <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)}
                           className="input-field w-full py-2 px-3"
                           placeholder="Enter password" required />
                </div>
                <div className="mb-6">
                    <label htmlFor="confirmPassword" className="field-label">Confirm Password:</label>
                    <input type="password" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                           className="input-field w-full py-2 px-3"
                           placeholder="Confirm password" required />
                </div>
                <div className="mb-6">
                    <label htmlFor="inviteCode" className="field-label">Invite Code:</label>
                    <input type="text" id="inviteCode" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)}
                           className="input-field w-full py-2 px-3"
                           placeholder="Enter invite code" required />
                </div>
                <div className="mb-6">
                    <label className="flex items-start sm:items-center gap-2 text-sm text-muted">
                        <input
                            type="checkbox"
                            checked={agreedToTerms}
                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                            className="mt-1 sm:mt-0"
                            required
                        />
                        <span>
                            I agree to the <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-info hover:underline">Privacy Policy</Link>
                            {' '}and <Link to="/terms-of-use" target="_blank" rel="noopener noreferrer" className="text-info hover:underline">Terms of Use</Link>.
                        </span>
                    </label>
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <button type="submit" className="btn btn-primary w-full sm:w-auto">
                        Create Account
                    </button>
                    <button type="button" onClick={() => onNavigate('Login')} className="btn btn-secondary w-full sm:w-auto">
                        Back to Login
                    </button>
                </div>
                {message && (
                    <p className="text-danger text-sm mt-4 text-center">{message}</p>
                )}
            </form>
        </div>
    );
};